import { line, noSteppers, radii, space, withAlpha } from "src/theme/ui";
import { fieldHeight } from "src/components/ui/field";
import styled from "styled-components";
import {
  InputNumber as AntInputNumber,
  Input as AntInput,
  Typography,
} from "antd";
/* IconContainer, DollarSign and Amount used to live here — an #e8f1ff chip,
   a 14px glyph defaulting to #000 and a bold amount defaulting to #000. No
   file imported any of the three; the components that look like them have
   their own copies under components/Deposit. */
import { IPAD } from "src/utils/constants/media";
import { colors } from "src/theme/colors";

export const FormContainer = styled.div`
  font-size: 0.75rem;
  width: 100%;

  @media (max-width: ${IPAD}px) {
    width: 100%;
  }
`;

/**
 * One row of a form.
 *
 * 14px between rows, matching the deposit form's own stack, and NOTHING at
 * the two ends: the last row's bottom margin sat under the actions band —
 * which is full-bleed to the dialog's edges by design — and left a sixteen
 * pixel strip of white below the footer in the withdraw dialog.
 */
export const FormElement = styled.div`
  margin: 14px 0;
  position: relative;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

/**
 * Every field on the deposit, withdraw and settings forms.
 *
 * They were bordered in a FULL accent line — primary[500], the same colour as
 * the submit button — so a screen of eight fields was a screen of eight
 * accent rectangles, and nothing on it could be emphasised because everything
 * already was. A disabled field then took an accent tint as its fill, which
 * in this product reads as selected rather than as unavailable.
 *
 * The product's field instead: white on a hairline, the accent kept for
 * focus, and a disabled field greyed the way a disabled thing is greyed
 * everywhere else.
 */
export const InputNumber = styled(AntInputNumber)`
  &.ant-input-number {
    width: 100%;
    height: ${fieldHeight.field}px;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.field};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &.ant-input-number .ant-input-number-input {
    height: ${fieldHeight.field - 2}px;
    font-size: 13px;
    color: ${colors.gray[900]};
  }

  &.ant-input-number:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-input-number-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  /* Greyed the way every other disabled thing in the product is greyed —
     the library leaves the resting edge in place, so a field that cannot be
     typed into looked exactly like one that can. */
  &.ant-input-number-disabled {
    background: ${colors.gray[50]};
    border-color: ${line.hairline};
    color: ${colors.gray[600]};
  }

  &.ant-input-number-disabled:hover {
    border-color: ${line.hairline};
  }

  &.ant-input-number-disabled .ant-input-number-input {
    color: ${colors.gray[600]};
    cursor: not-allowed;
  }

  ${noSteppers}
`;

/* A field's name, in ink. It was accent blue at 12px — the same colour as
   the field's own border and the submit button, which left the accent
   meaning nothing on these forms. */
export const Label = styled.label`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.gray[700]};
`;

export const ColContainer = styled.div<{ justify?: string }>`
  width: 100%;
  display: flex;
  justify-content: ${({ justify }) => (justify ? justify : "center")};
  gap: 8px;
  align-items: center;
  white-space: nowrap;

  @media (max-width: ${IPAD}px) {
    flex-direction: column;
  }
`;

export const MoneyIconContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-38px);
  font-weight: bold;
  color: ${colors.primary[500]};
  span {
    font-weight: normal;
    margin-top: -5px;
  }

  @media (max-width: ${IPAD}px) {
    display: none;
  }
`;

export const MoneyIcon = styled.img`
  align-items: center;
  width: 40px;
  height: 40px;
`;

/* Nothing. Three ems here was nearly fifty pixels of blank paper between the
   thing that introduces a form and the form itself; twenty was still forty
   once the first field's own top margin landed on top of it. The field
   carries the gap. */
export const Wrapper = styled.div`
  margin-top: 0;
`;

/**
 * A field's caption — its name, or its error.
 *
 * The error case was never actually coloured here. The rule that meant to do
 * it targeted `.ant-typography-danger` as a DESCENDANT, and the class sits on
 * this element itself, so it matched nothing; the colour then fell through to
 * the library's own #ff4d4f, at 80% opacity, under a stray closing brace that
 * broke everything after it. So an error was washed-out library red rather
 * than the product's.
 *
 * It is the theme's error step now, at full strength and a little heavier —
 * an error is the one thing on a form that should not be muted.
 */
export const Text = styled(Typography.Text)`
  &.ant-typography {
    /* Inherit, in the ordinary case. This span sits INSIDE the label, so a
       size and a colour of its own overrode the label's — the account select
       was captioned 12px/600 ink and the amount beside it 11.5px grey, from
       one form. The label decides; this only carries the error. */
    font-size: ${({ type }) => (type === "danger" ? "12px" : "inherit")};
    color: ${({ type }) => (type === "danger" ? colors.error[600] : "inherit")};
    opacity: 1;
    font-weight: ${({ type }) => (type === "danger" ? 500 : "inherit")};
  }
`;

export const Input = styled(AntInput)`
  &.ant-input,
  &.ant-input-affix-wrapper {
    height: ${fieldHeight.field}px;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.field};
    font-size: 13px;
    color: ${colors.gray[900]};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &.ant-input:hover,
  &.ant-input-affix-wrapper:hover {
    border-color: ${colors.gray[300]};
  }

  /* The accent, on focus only — which is the one moment a field is the
     subject of the screen. The library's default here was to remove the
     border entirely on focus, so the field lost its edge as you typed in
     it. */
  &.ant-input:focus,
  &.ant-input-focused,
  &.ant-input-affix-wrapper-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  &.ant-input[disabled],
  &.ant-input-disabled {
    background: ${colors.gray[50]};
    border-color: ${line.hairline};
    color: ${colors.gray[600]};
    cursor: default;
  }

  &.ant-input[disabled]:hover,
  &.ant-input-disabled:hover {
    border-color: ${line.hairline};
  }
`;

export const Password = styled(AntInput.Password)`
  &.ant-input-affix-wrapper {
    height: ${fieldHeight.field}px;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.field};
    box-shadow: none;
  }

  &.ant-input-affix-wrapper:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-input-affix-wrapper-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  &.ant-input-affix-wrapper .ant-input {
    height: auto;
    border: 0;
    box-shadow: none;
  }

  &.ant-input-affix-wrapper-disabled {
    background: ${colors.gray[50]};
    border-color: ${line.hairline};
    color: ${colors.gray[600]};
  }

  /**
   * The reveal toggle.
   *
   * Ant Design renders it as a bare 14px glyph with no target of its own, and
   * it was painted the accent — the one colour this product reserves for what
   * a reader can act on, spent here inside a field whose focus ring is
   * already accent. Football gives it a 28px square that lights up on hover
   * and takes the keyboard: revealing a password is a decision, and a
   * decision the mouse can make has to be reachable without one.
   */
  /* Ant Design 4.16 renders the eye as a bare svg inside the suffix, with the
     click handler on the glyph itself — so the suffix IS the target, and it
     is the suffix that has to become one. */
  &.ant-input-affix-wrapper .ant-input-suffix {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    margin-left: 4px;
    margin-right: -6px;
    border-radius: 7px;
    color: ${colors.gray[500]};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }

  &.ant-input-affix-wrapper .ant-input-suffix:hover {
    background: ${colors.gray[100]};
    color: ${colors.gray[900]};
  }

  /* The glyph takes the target's colour rather than antd's own 45% black. */
  .ant-input-password-icon {
    color: inherit;
  }
`;

/**
 * A form's action row.
 *
 * The buttons were being forced to `padding: 20px 0 !important` and a 1rem
 * label — an override that beat every control height in the product, so a
 * button in a form came out a different size from the same button anywhere
 * else, and one with no horizontal padding at all. Removed; the buttons state
 * their own size.
 *
 * Right-aligned rather than `space-between`, which pushed Cancel to one edge
 * of the form and the primary to the other with the whole measure between
 * them — two buttons that belong to one decision should sit together, with
 * the primary last.
 */
export const Action = styled.div<{ position?: string; $band?: boolean }>`
  margin: ${space.xl}px 0 0;

  /* Full-bleed, ruled off and sunk when the form is in a dialog — the footer
     every other dialog in the product finishes with. */
  ${({ $band }) =>
    $band
      ? `
        margin: ${space.lg}px -20px -20px;
        padding: 13px 20px;
        border-top: 1px solid ${line.hairline};
        background: ${colors.gray[25]};
      `
      : ""}
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => position || "flex-end"};
  gap: ${space.sm}px;

  button {
    min-width: 120px;
  }

  /* Stacks on a phone, not on a tablet.
     This was the IPAD breakpoint (769px), while the design system's own
     action row — ui/Dialog's DialogActions, which every other dialog in the
     product finishes with — stacks at 480px. So the same two buttons sat side
     by side in one dialog and stacked in the next at the same window width.
     Two buttons at a 120px minimum and an 8px gap need 248px; there is no
     reason to stack them at 600. */
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    width: 100% !important;

    button {
      width: 100%;
      min-width: 0;
    }
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

/**
 * The comment field, on the same field as everything else.
 *
 * It was a 5px box inside a full accent border — the same treatment the
 * number inputs carried before them, so the withdraw dialog showed a
 * hairline select above an accent-ringed textarea and read as two forms.
 * No fixed height: a multi-line field states a minimum and grows.
 */
export const TextArea = styled(AntInput.TextArea)`
  &.ant-input {
    min-height: 76px;
    padding: 9px 12px;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.field};
    font-size: 13px;
    color: ${colors.gray[900]};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &.ant-input:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-input:focus,
  &.ant-input-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  &.ant-input::placeholder {
    color: ${colors.gray[400]};
  }

  &.ant-input-disabled {
    background: ${colors.gray[50]};
    border-color: ${line.hairline};
    color: ${colors.gray[600]};
  }
`;

/* The password rules used to be drawn here — a wrapping row of 12px/300
   spans with a 6px dot, hidden until the field had a value and painted in the
   error colour for every rule not yet met. They are
   `components/ui/PasswordStrength` now: football's meter and checklist, which
   are visible from an empty field and reserve red for a password that is
   actually weak. */

/**
 * The mark on a field that has to be filled in.
 *
 * Quiet rather than red: it says "this one is needed", which is a fact about
 * the field, not an error the reader has already made. The error colour is
 * kept for when they actually get something wrong.
 *
 * Paired with a screen-reader word, because an asterisk alone is a
 * convention, not a label.
 */
export const RequiredMark = styled.span`
  margin-left: 2px;
  color: ${colors.gray[600]};
`;

export const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * A field's error, under the field.
 *
 * It used to live in the label row, opposite the field's name, and the name
 * was turned red alongside it — so a message of any length gave the reader
 * two red lines above an empty box, the first of which was the label. The
 * name stays ink and says what the field is; the message sits under the
 * control it is about, which is where the eye goes after trying to fill it
 * in.
 */
export const FieldError = styled.div`
  /* Doubled, so nothing scoped to the dialog body can outrank a single class
     and leave an error reading as ordinary text. */
  && {
    margin-top: 5px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    color: ${colors.error[600]};
  }
`;
